import type { HandFrame } from '../domain/types'
import { convertHandResult } from './convertResult'
import type { VisionEvent } from './protocol'

export interface VisionController {
  submit(video: HTMLVideoElement, timestampMs: number, mirrorX: boolean): void
  dispose(): void
}

interface VisionOptions {
  onFrame(frame: HandFrame): void
  onStatus(message: string): void
  onError(message: string): void
}

export async function createVisionController(options: VisionOptions): Promise<VisionController> {
  const appleMobile = /iPhone|iPad|iPod/i.test(navigator.userAgent) || (/Mac/i.test(navigator.platform) && navigator.maxTouchPoints > 1)
  if (appleMobile || typeof Worker === 'undefined' || typeof createImageBitmap !== 'function') {
    return createInlineController(options)
  }

  const worker = new Worker(new URL('./vision.worker.ts', import.meta.url), { type: 'module' })
  let busy = false
  let disposed = false
  let failed = false
  worker.onmessage = (message: MessageEvent<VisionEvent>) => {
    if (disposed) return
    const event = message.data
    if (event.type === 'ready') options.onStatus(`Two-hand tracking ready · ${event.delegate}`)
    else if (event.type === 'result') {
      busy = false
      options.onFrame(event.frame)
    } else {
      busy = false
      if (!event.recoverable) failed = true
      options.onError(event.message)
    }
  }
  worker.onerror = (event) => {
    if (disposed) return
    busy = false
    failed = true
    options.onError(event.message || 'The tracking worker could not start. Pointer controls remain available.')
  }
  worker.onmessageerror = () => {
    if (disposed) return
    busy = false
    failed = true
    options.onError('The tracking worker returned unreadable data. Pointer controls remain available.')
  }
  worker.postMessage({ type: 'init', wasmBaseUrl: '/mediapipe/wasm', modelAssetUrl: '/mediapipe/hand_landmarker.task' })

  return {
    submit(video, timestampMs, mirrorX): void {
      if (disposed || failed || busy || video.readyState < 2) return
      busy = true
      void createImageBitmap(video).then((frame) => {
        if (disposed) {
          frame.close()
          return
        }
        worker.postMessage({ type: 'frame', frame, timestampMs, mirrorX }, [frame])
      }).catch((error) => {
        busy = false
        options.onError(error instanceof Error ? error.message : 'Could not read a camera frame.')
      })
    },
    dispose(): void {
      disposed = true
      worker.onmessage = null
      worker.onerror = null
      worker.onmessageerror = null
      worker.postMessage({ type: 'dispose' })
      worker.terminate()
    }
  }
}

async function createInlineController(options: VisionOptions): Promise<VisionController> {
  const { FilesetResolver, HandLandmarker } = await import('@mediapipe/tasks-vision')
  const fileset = await FilesetResolver.forVisionTasks('/mediapipe/wasm', true)
  type InlineLandmarker = Awaited<ReturnType<typeof HandLandmarker.createFromOptions>>
  let landmarker: InlineLandmarker | null = await HandLandmarker.createFromOptions(fileset, {
    baseOptions: { modelAssetPath: '/mediapipe/hand_landmarker.task', delegate: 'CPU' },
    runningMode: 'VIDEO',
    numHands: 2,
    minHandDetectionConfidence: 0.55,
    minHandPresenceConfidence: 0.5,
    minTrackingConfidence: 0.5
  })
  let lastInferenceAt = -Infinity
  options.onStatus('Two-hand tracking ready · mobile')
  return {
    submit(video, timestampMs, mirrorX): void {
      if (!landmarker || timestampMs - lastInferenceAt < 66 || video.readyState < 2) return
      lastInferenceAt = timestampMs
      try {
        options.onFrame(convertHandResult(landmarker.detectForVideo(video, timestampMs), timestampMs, mirrorX))
      } catch (error) {
        options.onError(error instanceof Error ? error.message : 'Hand tracking paused.')
      }
    },
    dispose(): void {
      landmarker?.close()
      landmarker = null
    }
  }
}
